import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import FuelInvoiceTrendChart from './FuelInvoiceTrendChart';
import {
  calcFuelInvoiceTotal,
  createFleetFuelInvoice,
  deleteFleetFuelInvoice,
  fetchAllFleetFuelInvoices,
  getFleetFuelInvoiceErrorMessage,
  updateFleetFuelInvoice
} from '../../services/fleetFuelInvoicesService';
import {
  FUEL_INVOICE_MONTHS,
  FUEL_INVOICE_PAYMENT_STATUSES,
  buildFuelInvoiceTrendData,
  buildFuelInvoiceYearOptions,
  cellInputWidthCh,
  emptyFuelInvoiceForm,
  formatFleetFuelTSh,
  fuelDateInputToPaidLabel,
  fuelDatePaidToInputValue,
  fuelPaymentStatusClass,
  fuelProjectBadgeClass,
  getFleetFuelInvoiceProjectOptions,
  moneyFieldValue,
  normalizeFleetProjectFilter
} from '../../utils/fleetFuelInvoiceShared';
import '../../pages/FleetMaintenanceCostTracking.css';

/**
 * Fleet fuel invoices chart + editable table (same data as Fleet → Fuel tab).
 * @param {{ projectFilter?: string, yearFilter?: string, showChart?: boolean, onRowsChange?: (rows: object[]) => void }} props
 */
export default function FleetFuelInvoicesSection({
  projectFilter = 'All',
  yearFilter = 'All',
  showChart = true,
  onRowsChange
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const saveTimers = useRef({});
  const snapshotsRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllFleetFuelInvoices();
      data.forEach((row) => {
        snapshotsRef.current[row.id] = row;
      });
      setRows(data);
    } catch (err) {
      console.warn(err);
      setRows([]);
      setError(getFleetFuelInvoiceErrorMessage(err) || 'Failed to load fuel invoices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      Object.values(saveTimers.current).forEach((t) => t && clearTimeout(t));
    };
  }, [load]);

  useEffect(() => {
    onRowsChange?.(rows);
  }, [rows, onRowsChange]);

  const trendData = useMemo(
    () => buildFuelInvoiceTrendData(rows, { projectFilter, year: yearFilter }),
    [rows, projectFilter, yearFilter]
  );

  const filteredRows = useMemo(() => {
    const project = normalizeFleetProjectFilter(projectFilter);
    let list =
      project === 'all'
        ? rows
        : rows.filter((r) => String(r.project || '').trim() === project);
    if (yearFilter !== 'All') {
      list = list.filter((r) => String(r.year || '').trim() === String(yearFilter));
    }
    return list;
  }, [rows, projectFilter, yearFilter]);

  const projectOptions = useMemo(() => getFleetFuelInvoiceProjectOptions(rows), [rows]);
  const yearOptions = useMemo(
    () => buildFuelInvoiceYearOptions(rows.map((r) => r.year)),
    [rows]
  );

  const trendSubtitle =
    normalizeFleetProjectFilter(projectFilter) === 'all'
      ? yearFilter === 'All'
        ? 'Monthly fuel invoice totals (all projects)'
        : `Monthly fuel invoice totals · ${yearFilter}`
      : yearFilter === 'All'
        ? `Monthly fuel invoice totals · ${projectFilter}`
        : `Monthly fuel invoice totals · ${projectFilter} · ${yearFilter}`;

  const persistRow = async (rowId) => {
    const nextRow = snapshotsRef.current[rowId];
    if (!rowId || !nextRow) return;
    setSavingIds((prev) => new Set(prev).add(rowId));
    setError('');
    try {
      const updated = await updateFleetFuelInvoice(rowId, nextRow);
      snapshotsRef.current[rowId] = updated;
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(getFleetFuelInvoiceErrorMessage(err) || 'Failed to save fuel invoice.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const schedulePersist = (rowId) => {
    if (!rowId) return;
    const timers = saveTimers.current;
    if (timers[rowId]) clearTimeout(timers[rowId]);
    timers[rowId] = setTimeout(() => {
      delete timers[rowId];
      persistRow(rowId);
    }, 500);
  };

  const updateCell = (rowId, field, rawValue) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === rowId);
      if (idx < 0) return prev;
      const row = prev[idx];
      const moneyFields = new Set(['fuelAmount', 'managementFee', 'vat']);
      let nextValue = rawValue;
      if (moneyFields.has(field)) {
        const cleaned = String(rawValue ?? '').replace(/,/g, '').trim();
        nextValue = cleaned === '' ? 0 : Number(cleaned);
        if (!Number.isFinite(nextValue)) nextValue = row[field] || 0;
      }
      const patched = { ...row, [field]: nextValue };
      if (moneyFields.has(field)) {
        patched.totalInvoiceAmount = calcFuelInvoiceTotal(
          patched.fuelAmount,
          patched.managementFee,
          patched.vat
        );
      }
      snapshotsRef.current[rowId] = patched;
      schedulePersist(rowId);
      const next = [...prev];
      next[idx] = patched;
      return next;
    });
  };

  const addRow = async () => {
    setAdding(true);
    setError('');
    try {
      const minSort = rows.reduce((min, r) => Math.min(min, Number(r.sortOrder) || 0), 0);
      const created = await createFleetFuelInvoice({
        ...emptyFuelInvoiceForm(),
        sortOrder: minSort - 1
      });
      snapshotsRef.current[created.id] = created;
      setRows((prev) => [created, ...prev]);
    } catch (err) {
      setError(getFleetFuelInvoiceErrorMessage(err) || 'Failed to add fuel invoice row.');
    } finally {
      setAdding(false);
    }
  };

  const removeRow = async (row) => {
    if (!row?.id) return;
    const timers = saveTimers.current;
    if (timers[row.id]) {
      clearTimeout(timers[row.id]);
      delete timers[row.id];
    }
    try {
      await deleteFleetFuelInvoice(row.id);
      delete snapshotsRef.current[row.id];
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setError(getFleetFuelInvoiceErrorMessage(err) || 'Failed to delete.');
    }
  };

  return (
    <div className="fr-fleet-invoices-section">
      {showChart ? (
        <FuelInvoiceTrendChart
          data={trendData}
          loading={loading}
          subtitle={trendSubtitle}
        />
      ) : null}

      <div className="fmct-card fmct-table-card fr-fleet-invoices-table">
        <div className="fmct-table-toolbar">
          <div>
            <h3>Fuel invoices</h3>
            <p className="fmct-toolbar-sub">
              Same spreadsheet as Fleet → Fuel. Click any cell to edit; changes save automatically.
            </p>
          </div>
          <button
            type="button"
            className="fmct-btn fmct-btn-primary"
            onClick={addRow}
            disabled={adding}
          >
            <Plus className="h-4 w-4" /> {adding ? 'Adding…' : 'Add row'}
          </button>
        </div>
        {error ? <p className="fmct-fuel-invoice-error">{error}</p> : null}
        <div className="fmct-table-wrap">
          <table className="fmct-table fmct-table--fuel-invoices fmct-table--fuel-invoices-editable">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Month</th>
                <th>Year</th>
                <th>Project</th>
                <th>Fuel amount</th>
                <th>Management fee</th>
                <th>VAT</th>
                <th>Total invoice amount</th>
                <th>PO</th>
                <th>Payment status</th>
                <th>Date paid</th>
                <th>PR No - Jazzcom</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="fmct-table-empty">
                    Loading fuel invoices…
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="fmct-table-empty">
                    {rows.length === 0 ? (
                      <>No fuel invoices yet. Click <strong>Add row</strong> to start.</>
                    ) : (
                      <>No invoices match the current filters.</>
                    )}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const total = calcFuelInvoiceTotal(
                    row.fuelAmount,
                    row.managementFee,
                    row.vat
                  );
                  const saving = savingIds.has(row.id);
                  const projectSelectOptions = [
                    ...new Set(
                      [row.project, ...projectOptions]
                        .map((p) => String(p || '').trim())
                        .filter(Boolean)
                    )
                  ];
                  return (
                    <tr key={row.id} className={saving ? 'fmct-fuel-row-saving' : undefined}>
                      <td className="fmct-fuel-invoice-no-cell">
                        <input
                          className="fmct-cell-input fmct-cell-input--invoice-no"
                          type="text"
                          value={row.invoiceNo || ''}
                          onChange={(e) => updateCell(row.id, 'invoiceNo', e.target.value)}
                          title={row.invoiceNo || ''}
                        />
                      </td>
                      <td>
                        <select
                          className="fmct-cell-input"
                          value={row.month || ''}
                          onChange={(e) => updateCell(row.id, 'month', e.target.value)}
                        >
                          <option value="" />
                          {FUEL_INVOICE_MONTHS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="fmct-cell-input fmct-cell-input--year"
                          value={row.year || ''}
                          onChange={(e) => updateCell(row.id, 'year', e.target.value)}
                          aria-label="Year"
                        >
                          <option value="" />
                          {yearOptions.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="fmct-fuel-project-cell">
                        <div
                          className={`fmct-fuel-project-wrap${row.project ? '' : ' is-empty'}`}
                          style={{
                            width: `${cellInputWidthCh(row.project, 10)}ch`,
                            minWidth: `${cellInputWidthCh(row.project, 10)}ch`
                          }}
                        >
                          {row.project ? (
                            <span
                              className={`rental-project-badge ${fuelProjectBadgeClass(row.project)}`}
                            >
                              {row.project}
                            </span>
                          ) : null}
                          <select
                            className="fmct-cell-input fmct-cell-input--project"
                            value={row.project || ''}
                            onChange={(e) => updateCell(row.id, 'project', e.target.value)}
                            aria-label="Project"
                          >
                            <option value="" />
                            {projectSelectOptions.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="fmct-num">
                        <span className="fmct-cell-money">
                          {moneyFieldValue(row.fuelAmount) !== '' && (
                            <span className="fmct-cell-money-prefix">TSh</span>
                          )}
                          <input
                            className="fmct-cell-input fmct-cell-input--money"
                            type="text"
                            inputMode="numeric"
                            value={moneyFieldValue(row.fuelAmount)}
                            onChange={(e) => updateCell(row.id, 'fuelAmount', e.target.value)}
                          />
                        </span>
                      </td>
                      <td className="fmct-num">
                        <span className="fmct-cell-money">
                          {moneyFieldValue(row.managementFee) !== '' && (
                            <span className="fmct-cell-money-prefix">TSh</span>
                          )}
                          <input
                            className="fmct-cell-input fmct-cell-input--money"
                            type="text"
                            inputMode="numeric"
                            value={moneyFieldValue(row.managementFee)}
                            onChange={(e) =>
                              updateCell(row.id, 'managementFee', e.target.value)
                            }
                          />
                        </span>
                      </td>
                      <td className="fmct-num">
                        <span className="fmct-cell-money">
                          {moneyFieldValue(row.vat) !== '' && (
                            <span className="fmct-cell-money-prefix">TSh</span>
                          )}
                          <input
                            className="fmct-cell-input fmct-cell-input--money"
                            type="text"
                            inputMode="numeric"
                            value={moneyFieldValue(row.vat)}
                            onChange={(e) => updateCell(row.id, 'vat', e.target.value)}
                          />
                        </span>
                      </td>
                      <td className="fmct-num fmct-fuel-total-cell">
                        {total > 0 ? formatFleetFuelTSh(total) : ''}
                      </td>
                      <td className="fmct-fuel-po-cell">
                        <input
                          className="fmct-cell-input fmct-cell-input--po"
                          type="text"
                          value={row.po || ''}
                          onChange={(e) => updateCell(row.id, 'po', e.target.value)}
                          style={{ width: `${cellInputWidthCh(row.po)}ch` }}
                        />
                      </td>
                      <td className="fmct-fuel-status-cell">
                        <div className="fmct-fuel-status-wrap">
                          <span
                            className={`fmct-fuel-status-badge ${fuelPaymentStatusClass(
                              row.paymentStatus
                            )}`}
                          >
                            {row.paymentStatus || 'Not processed'}
                          </span>
                          <select
                            className="fmct-cell-input fmct-cell-input--status"
                            value={row.paymentStatus || 'Not processed'}
                            onChange={(e) =>
                              updateCell(row.id, 'paymentStatus', e.target.value)
                            }
                          >
                            {FUEL_INVOICE_PAYMENT_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="fmct-fuel-date-cell">
                        <div className={`fmct-cell-date-wrap${row.datePaid ? '' : ' is-empty'}`}>
                          {row.datePaid ? (
                            <span className="fmct-cell-date-label" aria-hidden>
                              {row.datePaid}
                            </span>
                          ) : null}
                          <input
                            className="fmct-cell-input fmct-cell-input--date"
                            type="date"
                            value={fuelDatePaidToInputValue(row.datePaid)}
                            onChange={(e) => {
                              const label = fuelDateInputToPaidLabel(e.target.value);
                              updateCell(row.id, 'datePaid', label);
                            }}
                          />
                        </div>
                      </td>
                      <td className="fmct-fuel-pr-jazzcom-cell">
                        <input
                          className="fmct-cell-input fmct-cell-input--pr-jazzcom"
                          type="text"
                          value={row.prNoJazzcom || ''}
                          onChange={(e) =>
                            updateCell(row.id, 'prNoJazzcom', e.target.value)
                          }
                        />
                      </td>
                      <td className="fmct-fuel-invoice-actions">
                        <button
                          type="button"
                          className="fmct-link-btn fmct-fuel-delete-btn"
                          onClick={() => removeRow(row)}
                          aria-label="Delete row"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
